/* BP search overlay.
   Owns every search entry point: the mobile bar button, the desktop header icon and
   any leftover [data-modal-toggle="#search-modal"] trigger. Those are intercepted in
   the CAPTURE phase with stopImmediatePropagation so the base theme's own modal never
   opens alongside ours.

   Results use Shopify's predictive search endpoint. Enter (or "See all results")
   falls through to the normal /search page, so nothing depends on this script. */
(function () {
  if (window.__bpSearchInit) return;
  window.__bpSearchInit = true;

  var DEBOUNCE = 220;
  var LIMIT = 8;

  function init() {
    var root = document.getElementById('bp-search');
    if (!root) return;

    var panel = root.querySelector('.bp-search__panel');
    var input = root.querySelector('[data-bp-search-input]');
    var list = root.querySelector('[data-bp-search-list]');
    var results = root.querySelector('[data-bp-search-results]');
    var popular = root.querySelector('[data-bp-search-popular]');
    var empty = root.querySelector('[data-bp-search-empty]');
    var loading = root.querySelector('[data-bp-search-loading]');
    var seeAll = root.querySelector('[data-bp-search-all]');
    var clear = root.querySelector('[data-bp-search-clear]');
    var scroller = root.querySelector('[data-bp-search-body]');
    var form = root.querySelector('form');
    var timer = null;
    var controller = null;
    var lastFocus = null;
    var lockedAt = 0;

    /* ---------- the sheet follows the VISIBLE viewport ----------

       With the keyboard up, iOS Safari does not shrink the page. It keeps the layout
       viewport at full height and pans a smaller visible area around inside it. A sheet
       pinned to the layout viewport is therefore taller than what you can see: its lower
       third is behind the keyboard, the last results cannot be reached, and dragging down
       there pans the visible area rather than scrolling the list — which is what "it
       scrolls the whole page" actually was.

       So the real numbers get published as custom properties and bp-search.css sizes the
       sheet from them, on the phone breakpoint only. They are cleared on close so nothing
       is left pinned to a stale keyboard height. */
    var vv = window.visualViewport;

    function syncViewport() {
      if (!vv) return;
      root.style.setProperty('--bp-search-vh', vv.height + 'px');
      root.style.setProperty('--bp-search-vt', vv.offsetTop + 'px');
    }

    function watchViewport(on) {
      if (!vv) return;
      if (on) {
        syncViewport();
        vv.addEventListener('resize', syncViewport);
        vv.addEventListener('scroll', syncViewport);
      } else {
        vv.removeEventListener('resize', syncViewport);
        vv.removeEventListener('scroll', syncViewport);
        root.style.removeProperty('--bp-search-vh');
        root.style.removeProperty('--bp-search-vt');
      }
    }

    /* ---------- keep a drag inside the list ----------

       `overscroll-behavior: contain` in the stylesheet covers Chrome and Safari 16+, but
       the older iOS versions this store still sees ignore it, and hand a drag that starts
       at either end of the list straight to the document. Cancelling the gesture at the
       boundary is the one thing every engine honours.

       A drag that is not over the list at all — the search bar, the padding around it — is
       cancelled outright: there is nothing there to scroll. Multi-touch is left alone so
       pinch-zoom still works. */
    var dragY = 0;
    if (panel) {
      panel.addEventListener('touchstart', function (e) {
        dragY = e.touches[0].clientY;
      }, { passive: true });

      panel.addEventListener('touchmove', function (e) {
        if (e.touches.length > 1) return;
        var inList = scroller && e.target.closest && e.target.closest('[data-bp-search-body]');
        if (!inList) { e.preventDefault(); return; }
        var dy = e.touches[0].clientY - dragY;
        var atTop = scroller.scrollTop <= 0;
        var atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
        if ((atTop && dy > 0) || (atBottom && dy < 0)) e.preventDefault();
      }, { passive: false });
    }

    /* ---------- open / close ----------

       The page behind has to be genuinely locked while this is open. `overflow: hidden` on
       the body — what bp-search.css used to rely on alone — is ignored by iOS Safari, so on
       a phone the results list would scroll, reach its end, and start dragging the page
       underneath it.

       The lock iOS respects is position: fixed (see bp-search.css), and a fixed body forgets
       where it was scrolled to. So the offset is captured here, held in `top` while open,
       and handed back on close. Get that pairing wrong and closing the overlay teleports
       the customer to the top of the page. */
    function open() {
      lastFocus = document.activeElement;
      lockedAt = window.pageYOffset || document.documentElement.scrollTop || 0;
      root.hidden = false;
      document.body.style.top = (-lockedAt) + 'px';
      document.body.classList.add('bp-search-open');
      watchViewport(true);
      /* iOS needs the focus in the same tick as the tap to raise the keyboard */
      input.focus({ preventScroll: true });
      input.select();
    }

    function close() {
      root.hidden = true;
      watchViewport(false);
      /* unlock BEFORE scrolling — the body has to be back in flow for scrollTo to land */
      document.body.classList.remove('bp-search-open');
      document.body.style.top = '';
      window.scrollTo(0, lockedAt);
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }

    /* A back-navigation can restore this page from the bfcache with the overlay still open
       and the body still pinned, which would leave the page unscrollable with nothing
       visible to blame. */
    window.addEventListener('pageshow', function (e) {
      if (e.persisted && !root.hidden) close();
    });

    /* every trigger on the page, before the theme's own handlers run.
       [data-bp-open-search] is the theme-wide hook: the mobile bar's search pill, and the
       query field + "Try another search" button on the results page, all carry it. */
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest(
        '[data-bp-open-search], [data-modal-toggle="#search-modal"], #site-control a.nav-search'
      );
      if (!trigger) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      open();
    }, true);

    root.querySelectorAll('[data-bp-search-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !root.hidden) close();
    });

    /* ---------- rendering ---------- */
    function money(value) {
      var n = parseFloat(String(value).replace(/[^0-9.]/g, ''));
      if (isNaN(n)) return '';
      return '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
    }

    function card(p) {
      var a = document.createElement('a');
      a.className = 'bp-search__item';
      a.href = p.url;

      var img = document.createElement('span');
      img.className = 'bp-search__item-media';
      if (p.featured_image && p.featured_image.url) {
        var i = document.createElement('img');
        /* BP perf: suggest.json returns the ORIGINAL upload — megabytes for a 64px thumb.
           The CDN resizes via a width param; 160 is 2× the box for retina. The URL usually
           carries ?v= already, hence the query-aware join. */
        var src = p.featured_image.url;
        i.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=160';
        i.alt = p.title || '';
        i.loading = 'lazy';
        img.appendChild(i);
      }

      var body = document.createElement('span');
      body.className = 'bp-search__item-body';

      if (p.vendor) {
        var v = document.createElement('span');
        v.className = 'bp-search__item-vendor';
        v.textContent = p.vendor;
        body.appendChild(v);
      }

      var t = document.createElement('span');
      t.className = 'bp-search__item-title';
      t.textContent = p.title;
      body.appendChild(t);

      var price = document.createElement('span');
      price.className = 'bp-search__item-price';
      price.textContent = money(p.price);
      if (p.compare_at_price && parseFloat(p.compare_at_price) > parseFloat(p.price)) {
        var s = document.createElement('s');
        s.textContent = money(p.compare_at_price);
        price.appendChild(document.createTextNode(' '));
        price.appendChild(s);
      }
      body.appendChild(price);

      a.appendChild(img);
      a.appendChild(body);
      return a;
    }

    /* The dropdown asks for products only — it is a product picker. The RESULTS PAGE it
       hands off to is not: bp-search-results renders matching pages and articles under the
       grid, so this link must not pin type=product or those become unreachable.

       options[prefix]=last matches the hidden input on the form, so "See all results" and
       pressing Enter now return the same set. They did not before. */
    function seeAllUrl(term) {
      return '/search?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last';
    }

    function show(products, term) {
      list.textContent = '';
      if (!products.length) {
        results.hidden = true;
        empty.hidden = false;
        return;
      }
      products.forEach(function (p) { list.appendChild(card(p)); });
      seeAll.href = seeAllUrl(term);
      seeAll.textContent = 'See all results for “' + term + '”';
      seeAll.hidden = false;
      results.hidden = false;
      empty.hidden = true;
    }

    function reset() {
      results.hidden = true;
      empty.hidden = true;
      loading.hidden = true;
      popular.hidden = false;
      list.textContent = '';
    }

    /* ---------- query ---------- */
    function query(term) {
      if (controller) controller.abort();
      controller = ('AbortController' in window) ? new AbortController() : null;

      var url = '/search/suggest.json?q=' + encodeURIComponent(term) +
        '&resources[type]=product&resources[limit]=' + LIMIT +
        '&resources[options][unavailable_products]=last';

      popular.hidden = true;
      loading.hidden = false;

      fetch(url, controller ? { signal: controller.signal } : undefined)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          loading.hidden = true;
          var products = (data.resources && data.resources.results && data.resources.results.products) || [];
          show(products, term);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          loading.hidden = true;
          /* network trouble: the form still works, so point at the real results page */
          show([], term);
        });
    }

    input.addEventListener('input', function () {
      var term = input.value.trim();
      clear.hidden = term.length === 0;
      clearTimeout(timer);
      if (term.length < 2) { reset(); return; }
      timer = setTimeout(function () { query(term); }, DEBOUNCE);
    });

    clear.addEventListener('click', function () {
      input.value = '';
      clear.hidden = true;
      reset();
      input.focus();
    });

    root.querySelectorAll('[data-bp-search-term]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        input.value = chip.getAttribute('data-bp-search-term');
        clear.hidden = false;
        input.focus();
        query(input.value);
      });
    });

    form.addEventListener('submit', function (e) {
      if (!input.value.trim()) e.preventDefault();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
